import React, { useMemo, useState } from 'react';
import { Quota, Criterion } from 'queries';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { ErrorMsg } from '@app/form';
import { useTranslation } from 'react-i18next';
import { CriterionRangeQuotaInput } from './CriterionRangeQuotaInput';
import { Link } from '@app/core';
import {
  isValidRange,
  checkCriterionRangeCoverage,
  sortRangeQuotas,
  getRangeMax,
} from '/lib/criterionQuotaUtils';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 2,
  },
  inputList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  warning: {
    marginTop: theme.marginBase / 2,
  },
}));

interface Props {
  criterion: Criterion;
  numberOfParticipantRequired: number;
  onChange: (val: Criterion) => void;
}

export const CriterionRangeQuotaList = ({
  criterion,
  numberOfParticipantRequired,
  onChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [rangeError, setRangeError] = useState<string | null>(null);
  const quotas = criterion.quotas ?? [];
  const setQuotas = (quotas: Quota[]) => {
    onChange({
      ...criterion,
      quotas,
    });
  };

  const overallRangeChanged = useMemo(() => {
    const overallRange = criterion.allowList?.[0];
    if (!isValidRange(overallRange)) return false;

    return !checkCriterionRangeCoverage(quotas, criterion);
  }, [criterion.allowList, quotas, t]);

  const handleQuotaChange = (index: number, newValue: number) => {
    const updated = [...quotas];
    updated[index] = { ...updated[index], value: newValue };
    setQuotas(updated);
  };

  const handleLimitChange = (index: number, newValue: number, type: string) => {
    const overallRange = criterion.allowList?.[0];
    if (!isValidRange(overallRange)) return;

    const updated = [...quotas];
    const item = updated[index].item;
    if (isValidRange(item)) {
      if (type === 'min') {
        updated[index] = {
          ...updated[index],
          item: { ...item, min: newValue },
        };
        if (index > 0) {
          const prevItem = updated[index - 1].item;
          if (isValidRange(prevItem)) {
            prevItem.max = newValue - 1;
            updated[index - 1] = { ...updated[index - 1], item: prevItem };
          }
        }
      }
      if (type === 'max') {
        updated[index] = {
          ...updated[index],
          item: { ...item, max: newValue },
        };
        if (index < updated.length - 1) {
          const nextItem = updated[index + 1].item;
          if (isValidRange(nextItem)) {
            nextItem.min = newValue + 1;
            updated[index + 1] = { ...updated[index + 1], item: nextItem };
          }
        }
      }
    }

    // Check for range boundary errors
    if (
      (type === 'min' && newValue < (overallRange.min ?? 0)) ||
      (type === 'max' && newValue > getRangeMax(overallRange.max))
    ) {
      setRangeError(t('editSession.targeting.quotas.exceedsRange'));
    } else if (!checkCriterionRangeCoverage(updated, criterion)) {
      setRangeError(t('editSession.targeting.quotas.rangeNotCovered'));
    } else {
      setRangeError(null);
    }

    setQuotas(updated);
  };

  const handleAddQuota = () => {
    const overallRange = criterion.allowList?.[0];
    if (!isValidRange(overallRange)) return;

    // Sort existing quotas by min value
    const sortedQuotas = sortRangeQuotas(quotas);

    // Find the first non-covered range
    let newMin = overallRange.min;
    let newMax = getRangeMax(overallRange.max);

    for (let i = 0; i < sortedQuotas.length; i++) {
      const currentQuota = sortedQuotas[i];
      if (!isValidRange(currentQuota.item)) continue;

      const currentMin = currentQuota.item.min ?? 0;
      const currentMax = getRangeMax(currentQuota.item.max);

      if (currentMin === newMin) {
        // If this quota starts at our current min, move our min to its max + 1
        newMin = currentMax + 1;
      } else {
        // We found a non-covered range, set our max to the current quota's min - 1
        newMax = currentMin - 1;
        break;
      }
    }

    //If the range is totally covered, we need to split the last quota
    if (
      (newMin ?? 0) > getRangeMax(newMax) ||
      quotas.some((q) => isValidRange(q.item) && q.item.max === null)
    ) {
      const lastQuota = sortedQuotas[sortedQuotas.length - 1];
      if (!isValidRange(lastQuota.item)) return;
      const min = lastQuota.item.min ?? 0;
      const max = getRangeMax(lastQuota.item.max);
      const mid = Math.floor((min + (max === Infinity ? 100 : max)) / 2);

      //the difference = 1, can't split
      if (min === mid || mid + 1 === max) {
        setRangeError(t('editSession.targeting.quotas.rangeCovered'));
        return;
      }

      // Split the last quota
      sortedQuotas[sortedQuotas.length - 1] = {
        ...lastQuota,
        item: { min: min, max: mid },
      };
      const newQuota: Quota = {
        item: { min: mid + 1, max: max === Infinity ? null : max },
        value: 0,
      };

      sortedQuotas.push(newQuota);
      setQuotas(sortedQuotas);

      return;
    }

    setRangeError(null);
    const newQuota: Quota = {
      item: {
        min: newMin || null,
        max: newMax === Infinity ? null : newMax || null,
      },
      value:
        numberOfParticipantRequired -
        sortedQuotas.reduce((acc, field) => acc + (field.value || 0), 0),
    };
    const updated = [...quotas];
    updated.push(newQuota);

    setQuotas(updated);
  };

  const handleRemoveQuota = (index: number) => {
    const updated = [...quotas];
    updated.splice(index, 1);

    if (!checkCriterionRangeCoverage(updated, criterion)) {
      setRangeError(t('editSession.targeting.quotas.rangeNotCovered'));
    } else {
      setRangeError(null);
    }

    setQuotas(updated);
  };

  const sum = quotas.reduce((acc, field) => acc + (field.value || 0), 0);
  const isInvalidMessage =
    sum !== numberOfParticipantRequired
      ? sum > numberOfParticipantRequired
        ? t('editSession.targeting.quotas.overly', {
            count: sum,
            required: numberOfParticipantRequired,
          })
        : t('editSession.targeting.quotas.insufficient', {
            count: sum,
            required: numberOfParticipantRequired,
          })
      : false;

  return (
    <div className={classes.container}>
      <div className={classes.inputList}>
        {quotas.map((field: Quota, index) => (
          <CriterionRangeQuotaInput
            value={field.value}
            min={isValidRange(field.item) ? field.item.min : null}
            max={isValidRange(field.item) ? field.item.max : null}
            rangeIsInvalid={rangeError !== null}
            onChange={(val: number) => handleQuotaChange(index, val)}
            onMinChange={(val: number) => handleLimitChange(index, val, 'min')}
            onMaxChange={(val: number) => handleLimitChange(index, val, 'max')}
            onRemove={() => handleRemoveQuota(index)}
          />
        ))}
      </div>
      {!!isInvalidMessage && (
        <ErrorMsg
          className={classes.warning}
          variant="warn"
          error={isInvalidMessage}
        />
      )}
      {(rangeError || overallRangeChanged) && (
        <ErrorMsg
          error={rangeError ?? t('editSession.targeting.quotas.rangeChanged')}
        />
      )}
      <Link addLeft onClick={handleAddQuota} type="button">
        {t('editSession.targeting.quotas.add')}
      </Link>
    </div>
  );
};
